import { useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import { emailGroupAnnouncement, fetchGroupAnnouncements } from '../services/groupService'
import { sendGroupMessage } from '../services/notificationService'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'

const DAY_MS = 24 * 60 * 60 * 1000

// Owner compose box. Always posts an announcement and notifies the bell; can
// also email it, at most once per group per day (enforced by group-email).
export default function GroupMessageDialog({ open, onOpenChange, group, currentUserId, onSent }) {
  const notify = useNotification()
  const [body, setBody] = useState('')
  const [alsoEmail, setAlsoEmail] = useState(false)
  const [sending, setSending] = useState(false)

  const lastEmailAt = group.last_email_at ? new Date(group.last_email_at) : null
  const nextEmailAt = lastEmailAt && Date.now() - lastEmailAt < DAY_MS
    ? new Date(lastEmailAt.getTime() + DAY_MS)
    : null

  const close = () => {
    onOpenChange(false)
    setBody('')
    setAlsoEmail(false)
  }

  const handleSend = async () => {
    const text = body.trim()
    if (!text) {
      notify.error('Message cannot be empty')
      return
    }
    setSending(true)
    try {
      const count = await sendGroupMessage(group.id, text)
      // send_group_message returns a count, so find the announcement it just
      // saved: the newest one from this owner.
      const announcements = await fetchGroupAnnouncements({ groupId: group.id }).catch(() => null)
      onSent?.(announcements)
      notify.success(`Posted and sent to ${count} member${count === 1 ? '' : 's'}`)

      if (alsoEmail) {
        const mine = announcements?.find(a => a.sender_id === currentUserId)
        try {
          if (!mine) throw new Error('Could not find the message to email')
          const result = await emailGroupAnnouncement({ announcementId: mine.id })
          notify.success(`Emailed ${result?.sent ?? 0} member${result?.sent === 1 ? '' : 's'}`)
        } catch (err) {
          console.error('Error emailing group message:', err)
          notify.error(`Posted, but not emailed: ${err.message}`)
        }
      }
      close()
    } catch (err) {
      console.error('Error sending group message:', err)
      notify.error(err?.message || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Message group members</DialogTitle>
          <DialogDescription>
            Post an announcement on the group page and notify every member of {group.name}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <label className="text-sm font-medium mb-1 block" htmlFor="group-message-body">Message</label>
          <textarea
            id="group-message-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write something to your group…"
            rows={4}
            maxLength={500}
            className="w-full rounded-md border border-input bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="text-xs text-muted-foreground text-right">{body.length}/500</div>

          <label className={`flex items-start gap-3 rounded-lg border p-3 ${nextEmailAt ? 'opacity-60' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              className="mt-1"
              checked={alsoEmail}
              disabled={!!nextEmailAt}
              onChange={(e) => setAlsoEmail(e.target.checked)}
            />
            <span>
              <span className="text-sm font-medium block">Also send by email</span>
              <span className="text-xs text-muted-foreground block">
                {nextEmailAt
                  ? `One email a day per group. You can email again ${nextEmailAt.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}.`
                  : "From \"you (via dub)\". Members' addresses stay private, and every email lets them stop group email or report it."}
              </span>
            </span>
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={close} disabled={sending}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending || !body.trim()}>
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
